import type { RefObject } from 'react';
import { Fragment, forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { IconList, InputSize } from '@snowit/icetypes';
import type { A11yEvent } from '@snowit/icetypes/events';

import useA11y from '@snowit/hooks/useA11y';
import useBreakpoints from '@snowit/hooks/useBreakpoints';
import Portal from '@snowit/hooks/usePortals';
import { Keyboard } from '@snowit/iceconst';
import useLanguage from '@snowit/system/useLanguage';
import { makeCSS } from '@snowit/utils/misc';
import testId from '@snowit/utils/testId';

import ButtonIcon from '../../Buttons/ButtonIcon';
import Divider from '../../Divider';
import Icon from '../../Icon';
import Typo from '../../Typography/Typo';
import { m } from '../../utils/Motion';
import Input from '../Input';

import cx from 'classnames';
import s from './InputSearch.module.scss';

import fuzzy from 'fuzzaldrin-plus';

export interface InputSearchOption {
  key: string;
  disabled?: boolean;
  icon?: IconList;
  label?: string;
  hint?: string;
  search: string;
  value?: string | number;
  divider?: boolean;
  test?: string;
}

export interface IInputSearch {
  size?: InputSize;
  icon?: IconList;
  label?: string;
  placeholder?: string;
  noResultsLabel?: string;
  hint?: string;
  value?: string | number;
  options: InputSearchOption[];
  error?: boolean;
  errorMsg?: string;
  name?: string;
  onChange?: (...arg: any[]) => void;
  onSelect?: (...arg: any[]) => void;
  onReset?: (...arg: any[]) => void;
  /** set to true means the filtering is done externally (using the onChange func), possibly via api */
  external?: boolean;
  resettable?: boolean;
  readOnly?: boolean;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  classNames?: {
    wrapper?: string;
    wrapInput?: string;
    input?: string;
    dropdown?: string;
  };
  test?: string;
}

const InputSearch = forwardRef((props: IInputSearch, ref: any) => {
  const {
    disabled,
    error,
    errorMsg,
    external,
    hint,
    icon,
    id,
    label,
    name,
    onChange,
    onSelect,
    onReset,
    options,
    placeholder,
    readOnly,
    required,
    test,
    value,
    size,
    resettable,
    noResultsLabel,
    classNames,
  } = props;
  const { t } = useLanguage();
  const { minWidth } = useBreakpoints();
  const a11yH = useA11y();

  const [wParent, setWParent] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState('');

  const parentRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);

  const _clickable = !disabled && !readOnly;
  const _resettable = _clickable && resettable;

  const focusParent = () => {
    (parentRef.current as HTMLElement)?.focus();
  };

  const navigableKeys = useMemo(() => {
    return options
      .filter((option) => !option.divider)
      .filter((option) => !option.disabled)
      .map((option) => option.key);
  }, [options]);

  // console.log('navigableKeys:', navigableKeys);
  // console.log('activeIndex:', activeIndex);
  // console.log('chiave selezionata:', navigableKeys[activeIndex]);

  useEffect(() => {
    const index = navigableKeys[activeIndex];
    if (itemsRef.current[index]) {
      itemsRef.current[index].focus();
    }
  }, [activeIndex, itemsRef.current, navigableKeys]);

  useEffect(() => {
    if (ref?.current) {
      ref.current = parentRef.current;
    }
  }, [ref?.current, parentRef.current]);

  useLayoutEffect(() => {
    if (parentRef.current) {
      setWParent(parentRef.current.offsetWidth);
    }
  }, []);

  const handleOpen = () => {
    setShowDropdown((prev) => {
      const next = !prev;
      setActiveIndex(next ? 0 : -1);
      return next;
    });
  };

  const handleClose = (
    event: A11yEvent<HTMLDivElement | HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    event.stopPropagation();
    setShowDropdown(false);
    setActiveIndex(-1);
    focusParent();
  };

  // const handleDropdown = () => setShowDropdown(!showDropdown);

  const handleChange = (args) => {
    if (disabled) return;
    setSearch(args.value);
    handleClose(args.event);
    // setShowDropdown(Boolean(args.value));
    if (typeof onChange === 'function') {
      onChange(args);
    }
  };

  const handleSelect = (val, option, event: A11yEvent<HTMLDivElement>) => {
    if (disabled) return;
    setSearch('');
    handleClose(event);
    // setShowDropdown(false);
    if (typeof onSelect === 'function') {
      onSelect({
        value: val,
        option,
        event,
        id,
        name,
      });
    }
  };

  const handleReset = () => {
    if (disabled) return;
    if (typeof onSelect === 'function') {
      onSelect({
        name,
        id,
        value: undefined,
      });
    }
    if (typeof onReset === 'function') {
      onReset({
        name,
        id,
        value: undefined,
      });
    }
    setSearch('');
    // setShowDropdown(true);
  };

  let motionVariants = {};

  if (minWidth.sm) {
    motionVariants = {
      open: { opacity: 1, height: 'auto' },
      collapsed: { opacity: 0, height: 0 },
    };
  } else {
    motionVariants = {
      open: { opacity: 1, translateY: '0%' },
      collapsed: { opacity: 1, translateY: '100%' },
    };
  }

  const renderLabel = () => {
    const found = options.find((o) => (o.value && o.value === value) || (o.key && o.key === value));

    if (found) return found.label;
    return search;
  };

  const renderInput = (
    parentRef: RefObject<any> | undefined = undefined,
    clickable = false,
    mobile = false,
  ) => (
    <Input
      classNames={{
        wrapper: classNames?.wrapper,
        wrapInput: classNames?.wrapInput,
        input: classNames?.input,
      }}
      fakeInput={mobile}
      label={label}
      placeholder={placeholder}
      ref={parentRef}
      disabled={disabled}
      required={required}
      type={'text'}
      onChange={handleChange}
      onClick={clickable && _clickable ? handleOpen : undefined}
      onKeyDown={(event) => {
        if (event.key === Keyboard.ENTER) {
          handleOpen();
        }
      }}
      value={renderLabel()}
      hint={hint}
      error={error}
      errorMsg={errorMsg}
      test={test}
      icon={icon}
      size={size}
      readOnly={readOnly}
      iconButton={'close'}
      onClickButton={value && _resettable ? handleReset : undefined}
    />
  );

  const renderModalHeader = () => {
    if (minWidth.sm) return null;
    return (
      <header className={s.header}>
        <Typo
          className={s.title}
          text={placeholder || label || ''}
          // text={placeholder ? placeholder : label}
          tag={'h2'}
          size={'df'}
          weight={'medium'}
          color={'text'}
        />
        <ButtonIcon
          classNames={{ button: s.close }}
          icon={'close'}
          onClick={() => setShowDropdown(false)}
          theme={'ghost'}
          size={'db'}
          color={'text'}
        />
      </header>
    );
  };

  const renderIconOption = (option: InputSearchOption) => {
    if (!option.icon) return null;
    return <Icon className={s.optionIcon} name={option.icon} />;
  };

  const renderLabelOption = (option: InputSearchOption) => {
    if (!option.label) return null;
    return (
      <Typo text={option.label} tag={'label'} size={'df'} lineHeight={'paragraph'} color={'text'} />
    );
  };

  const renderHintOption = (option: InputSearchOption) => {
    if (!option.hint) return null;
    return <Typo text={option.hint} tag={'p'} size={'sm'} color={'text-l'} />;
  };

  const renderOption = (option: InputSearchOption, index: number) => {
    const activeItem = index === activeIndex;

    if (option.divider) {
      return (
        <Divider
          key={option.key}
          classNames={{ wrapper: s.divider }}
          theme={'line'}
          spacing={[0.5]}
        />
      );
    }

    return (
      <div
        ref={(el) => {
          itemsRef.current[option.key] = el;
        }}
        key={option.key}
        className={cx(s.option, { [s.optionDisabled]: option.disabled })}
        onClick={(event) => handleSelect(option.key, option, event)}
        onKeyDown={(event) =>
          a11yH.handleListNav({
            event: event,
            active: activeIndex,
            total: navigableKeys.length,
            onEsc: handleClose,
            setIndex: setActiveIndex,
            onEnter: () => handleSelect(option.key, option, event),
          })
        }
        // selected={option.key === value}
        role={'listitem'}
        tabIndex={activeItem ? 0 : -1}
        // aria-selected={activeItem}
        aria-disabled={option.disabled}
        {...testId(test, option.key)}
      >
        <div className={s.contentOption}>
          {renderIconOption(option)}
          <div className={s.wrapLabel}>
            {renderLabelOption(option)}
            {renderHintOption(option)}
          </div>
        </div>
      </div>
    );
  };

  const renderOptions = () => {
    const opts =
      external || search.length < 3 ? options : fuzzy.filter(options, search, { key: 'search' });

    if (!opts.length) {
      return renderOption(
        {
          key: 'no_results',
          search: '',
          label: noResultsLabel || t('lzy.const.no_results'),
        },
        0,
      );
    }

    return opts.map(renderOption);
  };

  const renderDropdown = () => {
    return (
      <m.div
        className={cx(s.dropDown, classNames?.dropdown)}
        style={makeCSS({ '--w-parent': wParent })}
        initial='collapsed'
        animate='open'
        exit='collapsed'
        variants={motionVariants}
        transition={{
          type: 'tween',
          duration: 0.25,
          ease: [0.215, 0.61, 0.355, 1],
        }}
        role={'listbox'}
        aria-orientation={'vertical'}
      >
        {renderModalHeader()}
        {!minWidth.sm && <div className={s.innerInput}>{renderInput()}</div>}
        <div className={s.body} tabIndex={-1}>
          {renderOptions()}
        </div>
      </m.div>
    );
  };

  return (
    <Fragment>
      {renderInput(parentRef, true, !minWidth.sm)}
      <Portal
        mounted={showDropdown}
        content={renderDropdown()}
        bindTo={parentRef.current}
        onClose={() => setShowDropdown(false)}
        escToHide
        fullscreen={!minWidth.sm}
        clickToHide
        position={'adaptive'}
      />
    </Fragment>
  );
});

export default InputSearch;
